<template>
    <article class="user-profile">
        <header v-if="showHero" class="user-profile-hero">
            <figure class="user-profile-image">
                <img v-if="image" class="usr-image" :src="image" :alt="displayName" />
                <img v-else class="usr-image" src="@/assets/user.png" :alt="displayName" />
            </figure>
            <div class="user-profile-identity">
                <p v-if="eyebrow" class="user-profile-eyebrow">{{ eyebrow }}</p>
                <h3 class="user-profile-name">{{ displayName }}</h3>
                <p class="user-profile-username">@{{ username }}</p>
                <p v-if="tagline" class="user-profile-tagline">{{ tagline }}</p>
                <p v-if="showJoinDate" class="user-profile-join-date">
                    <i class="fas fa-calendar detail-icon" aria-hidden="true"></i>
                    <span>{{ t("Join date") }}: {{ renderDate(joinDate, t) }}</span>
                </p>
            </div>
        </header>

        <section v-if="showDetails && hasDetailsSection" class="user-profile-section">
            <h4 class="user-profile-section-title">{{ t("About") }}</h4>
            <dl class="user-profile-details">
                <div v-if="isFieldVisible(bio)" class="user-profile-detail">
                    <dt>
                        <i class="fas fa-info detail-icon" aria-hidden="true"></i>
                        <span>{{ t("Profile description") }}</span>
                    </dt>
                    <dd :class="{ 'is-empty': !bio }">{{ bio || "—" }}</dd>
                </div>
                <div v-if="isFieldVisible(location)" class="user-profile-detail">
                    <dt>
                        <i class="fas fa-location-dot detail-icon" aria-hidden="true"></i>
                        <span>{{ t("Location") }}</span>
                    </dt>
                    <dd :class="{ 'is-empty': !location }">{{ location || "—" }}</dd>
                </div>
                <div v-if="isFieldVisible(website)" class="user-profile-detail">
                    <dt>
                        <i class="fas fa-link detail-icon" aria-hidden="true"></i>
                        <span>{{ t("Website") }}</span>
                    </dt>
                    <dd :class="{ 'is-empty': !website }">
                        <a v-if="website" :href="website" target="_blank" rel="noopener noreferrer">{{ website }}</a>
                        <span v-else>—</span>
                    </dd>
                </div>
            </dl>
        </section>
    </article>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { renderDate } from "@/utils/time-utils";

defineOptions({ name: "UserProfileView" });

const {
    name = "",
    username = "",
    joinDate = "",
    bio = "",
    image = "",
    location = "",
    website = "",
    showEmptyFields = false,
    showHero = true,
    showDetails = true,
    showJoinDate = true,
    eyebrow = "",
    tagline = "",
} = defineProps<{
    name?: string;
    username?: string;
    joinDate?: string;
    bio?: string;
    image?: string;
    location?: string;
    website?: string;
    showEmptyFields?: boolean;
    showHero?: boolean;
    showDetails?: boolean;
    showJoinDate?: boolean;
    eyebrow?: string;
    tagline?: string;
}>();

const displayName = computed(() => name || username);

const isFieldVisible = (value?: string) => showEmptyFields || !!value;

const hasDetailsSection = computed(() => isFieldVisible(bio) || isFieldVisible(location) || isFieldVisible(website));
</script>

<style scoped>
.user-profile {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
}

.user-profile-hero {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 1.5rem;
}

.user-profile-image {
    flex-shrink: 0;
    width: 160px;
    height: 160px;
    margin: 0;
    border-radius: 50%;
}

.user-profile-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    background-color: var(--user-profile-image-background);
}

.user-profile-identity {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 0;
    flex: 1;
}

.user-profile-name {
    margin: 0;
    font-size: 1.75rem;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
}

.user-profile-username {
    margin: 0;
    font-size: 1.1rem;
    opacity: 0.75;
    overflow: hidden;
    text-overflow: ellipsis;
}

.user-profile-join-date {
    display: flex;
    align-items: center;
    margin: 0;
    font-size: 0.95rem;
    opacity: 0.85;
}

.user-profile-section {
    border-top: solid 1px var(--theme-border-color);
    padding-top: 1.25rem;
}

.user-profile-section-title {
    margin: 0 0 1rem;
    font-size: 1.1rem;
}

.user-profile-details {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin: 0;
}

.user-profile-detail {
    display: grid;
    grid-template-columns: minmax(140px, 200px) 1fr;
    gap: 0.75rem 1rem;
    align-items: start;
}

.user-profile-detail dt {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    margin: 0;
    font-weight: 600;
    font-size: 0.95rem;
}

.user-profile-detail dd {
    margin: 0;
    overflow-wrap: anywhere;
}

.user-profile-detail dd.is-empty {
    opacity: 0.5;
    font-style: italic;
}

.detail-icon {
    width: 1.25rem;
    text-align: center;
    flex-shrink: 0;
}

@media (max-width: 640px) {
    .user-profile-hero {
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    .user-profile-identity {
        align-items: center;
        width: 100%;
    }

    .user-profile-join-date {
        justify-content: center;
    }

    .user-profile-detail {
        grid-template-columns: 1fr;
        gap: 0.25rem;
    }
}

@media (max-width: 240px) {
    .user-profile-image {
        width: 100%;
        height: auto;
        aspect-ratio: 1;
    }
}
</style>
